import { useEffect, useMemo, useState } from "react";
import { PrimaryPopover, useAicoConnection } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, PopoverPosition } from "../../../../../Definitions";
import { colors, glyphs } from "../../../../../Styles";
import { LC_FEED_CURVE, LCFeedCurveArgs, LCFeedCurveResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lcFeedCurve";
import { GenericDeviceTable } from "../../../../../Components/ToolsComponents/genericDeviceDataTable";
import NewButton from "../../../../../Components/BaseComponents/newButton";
import ResetTableModal from "../../../../../Components/ToolsComponents/Modals/resetTableModal";
import { LC_TABLE_IDS } from "../../../../../Definitions/lcTableIds";
import { useLazyQuery } from "@apollo/client";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "feed_limit", accessor: "feed_limit", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "pass_feed", accessor: "pass_feed", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "stimuli", accessor: "stimuli", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "milestoneCount", accessor: "milestoneCount", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "componentCount", accessor: "componentCount", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "feedcurve_milestones", accessor: "feedcurve_milestones", type: ListData_ValueTypes.Div, sort: false },
	{ headerValue: "feedcurve_components", accessor: "feedcurve_components", type: ListData_ValueTypes.Div, sort: false },
]

export default function FeedCurveDataTable() {
	const [isResetTableModalOpen, setIsResetTableModalOpen] = useState<boolean>(false);

	const { state: connectionState } = useAicoConnection();

	const [fetchData, {data, loading, error }] = useLazyQuery<LCFeedCurveResponse, LCFeedCurveArgs>(LC_FEED_CURVE, { fetchPolicy: "network-only" });

	useEffect(() => {
		if (connectionState.deviceConnection) {
			fetchData({variables: connectionState.deviceConnection});
		}
	}, [connectionState.deviceConnection]);

	const tableData = useMemo(() => {
		return data?.controller.lcFeedCurve?.map((e, i) => {
			let milestoneCell: React.ReactNode | undefined = undefined;
			let componentCell: React.ReactNode | undefined = undefined;

			if (e.feedcurve_milestones.length) {
				milestoneCell = (
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<PrimaryPopover
							content={
								<div style={{ width: "fit-content", height: "fit-content", margin: "-5px 0px", cursor: "pointer" }}>
									<glyphs.Note fillColor={colors.greyDarkPrimary} />
								</div>
							}
							overrides={{
								popoverPosition: PopoverPosition.bottom
							}}
							popoverHeader={"milestones"}
							popoverContent={
								<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", color: colors.whitePrimary }}>
									Milestone ID | Milestone Index
									{e.feedcurve_milestones.map((ele, index) =>
										<div key={index}>{ele.milestoneId} : {ele.milestoneIndex}</div>
									)}
								</div>
							}
						/>
					</div>
				);
			}

			if (e.feedcurve_components.length) {
				componentCell = (
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<PrimaryPopover
							content={
								<div style={{ width: "fit-content", height: "fit-content", margin: "-5px 0px", cursor: "pointer" }}>
									<glyphs.Note fillColor={colors.greyDarkPrimary} />
								</div>
							}
							overrides={{
								popoverPosition: PopoverPosition.bottom
							}}
							popoverHeader={"components"}
							popoverContent={
								<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", color: colors.whitePrimary }}>
									Component ID | g/min
									{e.feedcurve_components.map((ele, index) =>
										<div key={index}>{ele.componentId} : {ele.gram_per_minute}</div>
									)}
								</div>
							}
						/>
					</div>
				);
			}

			return {
				...e,
				id: e.feedcurve_id,
				feedcurve_milestones: milestoneCell,
				feedcurve_components: componentCell
			}
		}) ?? [];
	}, [data]);

	return (
		<>
			<GenericDeviceTable
				title="Feed Curves"
				columnsData={columnsData}
				data={tableData}
				loading={loading}
				error={error}
				onFetch={() => connectionState.deviceConnection && fetchData({ variables: connectionState.deviceConnection })}
				tabButtons={[
					<NewButton
						key="Reset table"
						content="Reset Table"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>
				]}
			/>
			{connectionState.deviceConnection && 
				<ResetTableModal
					isModalOpen={isResetTableModalOpen}
					closeModal={()=>{ setIsResetTableModalOpen(false) }}
					onSuccess={() => fetchData({ variables:connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.feedcurves}
				/>
			}
		</>
	)
}